import React from "react";
import { HelpCircle, FileCheck2, BadgeCheck, ShieldCheck } from "lucide-react";
import { useT } from "@/lib/i18n";

const ICONS = [HelpCircle, FileCheck2, BadgeCheck, ShieldCheck];
const CLS = ["tier0", "tier1", "tier2", "tier3"];

const C = {
  en: {
    kicker: "The product", h1: "How we verify",
    lead: "Four levels. We always tell you which one you're looking at — with a colour, an icon and a text label, never colour alone.",
    tiers: [
      ["Self-declared", "The organisation entered this themselves. We haven't checked it yet. Treat it the way you'd treat any unverified claim."],
      ["Documents on file", "They've uploaded their certificates and a person on our team has read them. The papers exist and match what's claimed."],
      ["Registry-matched", "We've cross-checked their registration numbers against the issuing authority's own records: NITI Aayog Darpan, the MCA's CSR list, income-tax exemption records."],
      ["Field-verified", "Someone from our team went there. We show you the date, who went, and photographs from the visit."],
    ],
    checkT: "What we check",
    check: ["DARPAN ID (NITI Aayog)", "CSR-1 registration number (MCA)", "12A or 10(23C) income-tax registration", "80G approval and its expiry date", "FCRA registration, where foreign funding is involved", "Registration type and date of incorporation", "The last three years of audited accounts"],
    promiseT: "What we can't promise",
    promise: "Verification means the paperwork is real and the work is visible. It is not an audit and not a guarantee of impact. We tell you what we checked and when. The decision stays yours — which is why we build every listing to be visited in person.",
    expiryT: "Credentials expire. We track that.",
    expiry: "80G approvals lapse. Registrations get revoked. We flag any listing within 90 days of expiry, email the NGO, and automatically demote the badge and add a 'credential lapsed' notice once it expires. A directory that goes stale is worse than no directory at all.",
    ruleT: "The July 2025 CSR rule",
    rule: "Since the Companies (CSR Policy) Amendment Rules 2025 (in force 14 July 2025), an implementing agency needs 12A or 10(23C) plus a three-year track record to register on CSR-1 — unless set up by the funding company or a government body. NGOs that fail this cannot receive direct CSR funds and can only work as sub-partners. We surface this on every listing.",
  },
  ta: {
    kicker: "தயாரிப்பு", h1: "நாங்கள் எப்படி சரிபார்க்கிறோம்",
    lead: "நான்கு நிலைகள். நீங்கள் எதைப் பார்க்கிறீர்கள் என்பதை எப்போதும் சொல்கிறோம் — நிறம், சின்னம், உரை பெயர்ச்சீட்டு மூலம்; நிறத்தால் மட்டும் அல்ல.",
    tiers: [
      ["சுய அறிவிப்பு", "அமைப்பே இதை உள்ளிட்டது. நாங்கள் இன்னும் சரிபார்க்கவில்லை. சரிபார்க்கப்படாத கூற்றாக கருதுங்கள்."],
      ["ஆவணம் சரிபார்க்கப்பட்டது", "அவர்கள் சான்றிதழ்களைப் பதிவேற்றியுள்ளனர், எங்கள் குழு அவற்றைப் படித்துள்ளது. ஆவணங்கள் உள்ளன, கூற்றுடன் பொருந்துகின்றன."],
      ["பதிவேடு பொருந்தியது", "அவர்களின் பதிவு எண்களை வழங்கும் அதிகார பதிவேடுகளுடன் சரிபார்த்துள்ளோம்: நிதி ஆயோக் தர்பன், MCA CSR பட்டியல், வருமான வரி விலக்கு பதிவுகள்."],
      ["நேரில் சரிபார்க்கப்பட்டது", "எங்கள் குழுவினர் அங்கு சென்றனர். தேதி, சென்றவர், முகாம் புகைப்படங்களைக் காட்டுகிறோம்."],
    ],
    checkT: "நாங்கள் சரிபார்ப்பவை",
    check: ["தர்பன் ஐடி (நிதி ஆயோக்)", "CSR-1 பதிவு எண் (MCA)", "12A அல்லது 10(23C) வருமான வரி பதிவு", "80G ஒப்புதல் மற்றும் அதன் காலாவதி தேதி", "FCRA பதிவு (வெளிநாட்டு நிதி இருந்தால்)", "பதிவு வகை மற்றும் தொடங்கிய தேதி", "கடந்த மூன்று ஆண்டு தணிக்கை கணக்குகள்"],
    promiseT: "நாங்கள் உறுதியளிக்க முடியாதவை",
    promise: "சரிபார்ப்பு என்பது ஆவணம் உண்மை என்பதும் பணி தெரிகிறது என்பதும் ஆகும். இது தணிக்கை அல்ல, தாக்கத்திற்கு உத்தரவாதம் அல்ல. நாங்கள் என்ன சரிபார்த்தோம் எப்போது என்று சொல்கிறோம். முடிவு உங்களுடையது — அதனால்தான் ஒவ்வொரு பட்டியலையும் நேரில் பார்க்க வடிவமைக்கிறோம்.",
    expiryT: "சான்றுகள் காலாவதியாகும். நாங்கள் கண்காணிக்கிறோம்.",
    expiry: "80G ஒப்புதல்கள் காலாவதியாகும். பதிவுகள் ரத்தாகும். காலாவதிக்கு 90 நாட்களுக்குள் உள்ள பட்டியலைக் குறிக்கிறோம், என்.ஜி.ஓ-விற்கு மின்னஞ்சல் அனுப்புகிறோம், காலாவதியானதும் பேட்ஜை தானாகக் குறைத்து 'சான்று காலாவதி' குறிப்பைச் சேர்க்கிறோம். பழைய அடைவு, அடைவே இல்லாததை விட மோசம்.",
    ruleT: "ஜூலை 2025 CSR விதி",
    rule: "நிறுவனங்கள் (CSR கொள்கை) திருத்த விதிகள் 2025 (14 ஜூலை 2025 முதல் அமலில்) படி, ஒரு செயலாக்க அமைப்பிற்கு 12A அல்லது 10(23C) மற்றும் மூன்று ஆண்டு அனுபவம் தேவை — நிதியளிக்கும் நிறுவனம் அல்லது அரசால் அமைக்கப்பட்டால் தவிர. இதைப் பூர்த்தி செய்யாத என்.ஜி.ஓ-க்கள் நேரடி CSR நிதியைப் பெற முடியாது, துணை-கூட்டாளராக மட்டுமே செயல்பட முடியும். இதை ஒவ்வொரு பட்டியலிலும் காட்டுகிறோம்.",
  },
  hi: {
    kicker: "यही उत्पाद है", h1: "हम कैसे सत्यापित करते हैं",
    lead: "चार स्तर। आप जो देख रहे हैं वह हम हमेशा बताते हैं — रंग, आइकन और टेक्स्ट लेबल के साथ; केवल रंग से कभी नहीं।",
    tiers: [
      ["स्व-घोषित", "संस्था ने यह स्वयं दर्ज किया है। हमने अभी जाँचा नहीं है। इसे किसी असत्यापित दावे की तरह लें।"],
      ["दस्तावेज़ फ़ाइल पर", "उन्होंने अपने प्रमाणपत्र अपलोड किए हैं और हमारी टीम के एक व्यक्ति ने उन्हें पढ़ा है। कागज़ मौजूद हैं और दावे से मेल खाते हैं।"],
      ["रजिस्ट्री-मिलान", "हमने उनके पंजीकरण नंबरों को जारी करने वाले प्राधिकरण के रिकॉर्ड से मिलाया है: नीति आयोग दर्पण, MCA की CSR सूची, आयकर छूट रिकॉर्ड।"],
      ["स्थल पर सत्यापित", "हमारी टीम का कोई व्यक्ति वहाँ गया। हम तारीख, कौन गया, और दौरे की तस्वीरें दिखाते हैं।"],
    ],
    checkT: "हम क्या जाँचते हैं",
    check: ["दर्पण आईडी (नीति आयोग)", "CSR-1 पंजीकरण संख्या (MCA)", "12A या 10(23C) आयकर पंजीकरण", "80G अनुमोदन और उसकी समाप्ति तिथि", "FCRA पंजीकरण, जहाँ विदेशी निधि शामिल हो", "पंजीकरण प्रकार और निगमन की तिथि", "पिछले तीन वर्षों के लेखा-परीक्षित खाते"],
    promiseT: "हम क्या वादा नहीं कर सकते",
    promise: "सत्यापन का अर्थ है कि कागज़ासली हैं और काम दिखाई देता है। यह ऑडिट नहीं है और प्रभाव की गारंटी नहीं है। हम बताते हैं कि हमने क्या और कब जाँचा। निर्णय आपका रहता है — इसीलिए हम हर लिस्टिंग को व्यक्तिगत रूप से देखने योग्य बनाते हैं।",
    expiryT: "प्रमाणपत्र समाप्त होते हैं। हम उस पर नज़र रखते हैं।",
    expiry: "80G अनुमोदन समाप्त होते हैं। पंजीकरण रद्द होते हैं। समाप्ति के 90 दिनों के भीतर किसी भी लिस्टिंग को हम फ़्लैग करते हैं, एनजीओ को ईमेल करते हैं, और समाप्त होते ही बैज को स्वतः घटाकर 'प्रमाणपत्र समाप्त' सूचना जोड़ते हैं। बासी निर्देशिका, कोई निर्देशिका न होने से बुरी है।",
    ruleT: "जुलाई 2025 का CSR नियम",
    rule: "कंपनी (CSR नीति) संशोधन नियम 2025 (14 जुलाई 2025 से प्रभावी) के अनुसार, एक कार्यान्वयन एजेंसी को CSR-1 पर पंजीकरण हेतु 12A या 10(23C) और तीन-वर्ष का ट्रैक रिकॉर्ड चाहिए — जब तक कि वह वित्तपोषक कंपनी या सरकारी निकाय द्वारा स्थापित न हो। जो एनजीओ यह पूरा नहीं करते वे सीधे CSR निधि नहीं ले सकते, केवल उप-भागीदार के रूप में काम कर सकते हैं। हम इसे हर लिस्टिंग पर दिखाते हैं।",
  },
};

export default function Verification() {
  const { lang } = useT();
  const c = C[lang] || C.en;
  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
      <span className="text-xs font-semibold uppercase tracking-wide text-terracotta">{c.kicker}</span>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-brand mt-2">{c.h1}</h1>
      <p className="text-stone-600 mt-3 measure text-lg">{c.lead}</p>

      <div className="grid sm:grid-cols-2 gap-5 mt-10">
        {c.tiers.map(([title, body], n) => {
          const Icon = ICONS[n]; const cls = CLS[n];
          return (
            <div key={n} className={`rounded-2xl border p-6 bg-${cls}-bg border-${cls}-border`} data-testid={`verification-tier-${n}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`text-${cls}-icon`} size={22} strokeWidth={2.4} />
                <span className={`text-${cls}-text font-bold`}>Tier {n} · {title}</span>
              </div>
              <p className={`text-sm text-${cls}-text`}>{body}</p>
            </div>
          );
        })}
      </div>

      <section className="mt-12 grid md:grid-cols-2 gap-8">
        <div>
          <h2 className="text-xl font-bold text-slate-brand mb-2">{c.checkT}</h2>
          <ul className="text-sm text-stone-700 space-y-1.5 list-disc pl-5">
            {c.check.map((x) => <li key={x}>{x}</li>)}
          </ul>
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-brand mb-2">{c.promiseT}</h2>
          <p className="text-sm text-stone-700 measure">{c.promise}</p>
        </div>
      </section>

      <div className="mt-10 rounded-xl bg-amber-950 text-amber-50 p-6">
        <h2 className="font-bold mb-1">{c.expiryT}</h2>
        <p className="text-sm text-amber-100/90 measure">{c.expiry}</p>
      </div>

      <div className="mt-10 rounded-xl border border-stone-200 bg-white p-6">
        <h2 className="font-bold text-slate-brand mb-1">{c.ruleT}</h2>
        <p className="text-sm text-stone-700 measure">{c.rule}</p>
      </div>
    </div>
  );
}
